/**
 * Facturi — Universal invoice entry with smart routing:
 *   • Produs Agricol  → updates product stock & price in catalog
 *   • Motorină        → adds purchase to motorina tank
 *   • Piesă/Reparație → asks which utilaj → adds repair to equipment
 *   • Altele          → recorded as cost only
 *
 * Tabs: Facturi | Produse & Stoc
 */
import React, { useState, useEffect, useCallback } from 'react';
import universalDataService from '../services/universalDataService';
import { db } from '../config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const TIPURI_FACTURA = ['Factură', 'Aviz', 'Chitanță', 'Bon fiscal'];
const STATUSURI = ['Plătită', 'Neachitată', 'Scadentă'];
const STATUS_STYLE = {
  'Plătită':   { background: '#dcfce7', color: '#166534', border: '1px solid #86efac' },
  'Neachitată':{ background: '#fef9c3', color: '#854d0e', border: '1px solid #fde047' },
  'Scadentă':  { background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' },
};

const CATEGORII = [
  { id: 'produs',    label: '🌾 Produs Agricol', color: '#166534', bg: '#dcfce7' },
  { id: 'motorina',  label: '⛽ Motorină',        color: '#92400e', bg: '#fef3c7' },
  { id: 'reparatie', label: '🔧 Reparație/Piesă', color: '#4c1d95', bg: '#ede9fe' },
  { id: 'altele',    label: '📋 Altele',           color: '#1e3a5f', bg: '#dbeafe' },
];

const CATEGORII_PRODUS = ['Semințe', 'Îngrășăminte', 'Erbicide', 'Insecticide', 'Fungicide', 'Altele'];

const CROP_TYPES = [
  'Porumb', 'Grâu', 'Rapiță', 'Floarea-soarelui', 'Sfeclă de zahăr',
  'Lucernă', 'Soia', 'Orz', 'Ovăz', 'Cartofi', 'Legume', 'Fânețe',
  'Toate culturile', 'Teren necultivat',
];

// Map from our product categories to StockAdjustment productType names
const CAT_TO_SA = {
  'Semințe': 'Samanta', 'Îngrășăminte': 'Îngrășământ',
  'Erbicide': 'Erbicide', 'Insecticide': 'Insecticide',
  'Fungicide': 'Fungicide', 'Altele': 'Adjuvant',
};

const emptyLinie = () => ({
  id: Date.now() + Math.random(),
  categorie: 'produs',
  productId: '', productName: '', qty: '', unit: '', unitPrice: '',
  liters: '', pricePerLiter: '',
  utilajId: '', utilajName: '', repairDesc: '', repairCost: '',
  repairAmortType: 'current', repairAmortYears: '1',
  altDesc: '', altCost: '',
  targetCrop: '', consumPerHa: '',
  total: 0,
});

const emptyFactura = () => ({
  date: new Date().toISOString().split('T')[0],
  number: '', supplier: '', company: '', tipDoc: 'Factură', status: 'Plătită', note: '',
  linii: [emptyLinie()],
  _createdAt: new Date().toISOString(),
});

const emptyProdus = () => ({ name: '', category: '', unit: 'kg', notes: '' });

// Rebuild stock from facturi
const buildStock = (produse, facturi) => {
  const stock = {};
  produse.forEach(p => { stock[p.id] = { ...p, stoc: 0, lastPrice: 0 }; });
  facturi.forEach(f => {
    (f.linii || []).forEach(l => {
      if (l.categorie !== 'produs' || !l.productId) return;
      if (!stock[l.productId]) return;
      stock[l.productId].stoc += parseFloat(l.qty) || 0;
      stock[l.productId].lastPrice = parseFloat(l.unitPrice) || stock[l.productId].lastPrice;
    });
  });
  return Object.values(stock);
};

// ── Sync a saved Factura record into the StockAdjustment invoices store ──────
const syncToStockAdjustment = async (record, produse) => {
  try {
    const farmId = localStorage.getItem('currentFarmId');
    if (!farmId) return;

    // Map Factura lines → StockAdjustment items (skip reparatii)
    const items = (record.linii || []).filter(l => l.categorie !== 'reparatie').map(l => {
      if (l.categorie === 'motorina') {
        return {
          productType: 'Motorină', productName: 'Motorină',
          quantity: parseFloat(l.liters) || 0,
          unitPrice: parseFloat(l.pricePerLiter) || 0,
          unit: 'litri',
          targetCrop: 'Toate culturile', targetCrops: [],
          consumptionPerHa: l.consumPerHa ? parseFloat(l.consumPerHa) : null,
          totalValue: (parseFloat(l.liters)||0) * (parseFloat(l.pricePerLiter)||0),
        };
      } else if (l.categorie === 'produs') {
        const prod = produse.find(p => p.id === l.productId);
        const productType = CAT_TO_SA[prod?.category] || prod?.category || 'Adjuvant';
        return {
          productType,
          productName: l.productName || prod?.name || '—',
          quantity: parseFloat(l.qty) || 0,
          unitPrice: parseFloat(l.unitPrice) || 0,
          unit: l.unit || prod?.unit || 'kg',
          targetCrop: l.targetCrop || 'Nespecificat', targetCrops: l.targetCrop ? [l.targetCrop] : [],
          consumptionPerHa: l.consumPerHa ? parseFloat(l.consumPerHa) : null,
          totalValue: (parseFloat(l.qty)||0) * (parseFloat(l.unitPrice)||0),
        };
      } else {
        return {
          productType: 'Adjuvant', productName: l.altDesc || 'Altele',
          quantity: 1, unitPrice: parseFloat(l.altCost) || 0, unit: 'buc',
          targetCrop: 'Nespecificat', targetCrops: [],
          consumptionPerHa: null,
          totalValue: parseFloat(l.altCost) || 0,
        };
      }
    }).filter(i => i.totalValue > 0);

    const saInvoice = {
      id: String(record.id),
      number: record.number || String(record.id),
      company: record.company || '',
      date: record.date,
      status: record.status || 'Plătită',
      supplier: record.supplier || '',
      items,
      totalValue: items.reduce((s, i) => s + i.totalValue, 0),
      allocated: true, allocationPercentage: 100,
      createdAt: record._createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      _fromFacturi: true,
    };

    const docRef = doc(db, 'farmData', farmId);
    const snap = await getDoc(docRef);
    const cur = snap.exists() ? snap.data() : {};
    const existing = cur.invoices?.data || [];
    const clean = existing.filter(inv => String(inv.id) !== String(record.id));
    const updated = [...clean, saInvoice];
    await setDoc(docRef, { ...cur, invoices: { data: updated, lastModified: new Date().toISOString() } });
    const wrapper = { data: updated, lastModified: new Date().toISOString(), synced: true, farmId, dataType: 'invoices' };
    localStorage.setItem(`invoices_${farmId}`, JSON.stringify(wrapper));
  } catch (e) {
    console.warn('syncToStockAdjustment failed (non-critical):', e.message);
  }
};

const removeFromStockAdjustment = async (invoiceId) => {
  try {
    const farmId = localStorage.getItem('currentFarmId');
    if (!farmId) return;
    const docRef = doc(db, 'farmData', farmId);
    const snap = await getDoc(docRef);
    const cur = snap.exists() ? snap.data() : {};
    const updated = (cur.invoices?.data || []).filter(inv => String(inv.id) !== String(invoiceId));
    await setDoc(docRef, { ...cur, invoices: { data: updated, lastModified: new Date().toISOString() } });
    const wrapper = { data: updated, lastModified: new Date().toISOString(), synced: true, farmId, dataType: 'invoices' };
    localStorage.setItem(`invoices_${farmId}`, JSON.stringify(wrapper));
  } catch (e) {
    console.warn('removeFromStockAdjustment failed (non-critical):', e.message);
  }
};

const Inventory = () => {
  const [facturi, setFacturi]   = useState([]);
  const [produse, setProduse]   = useState([]);
  const [utilaje, setUtilaje]   = useState([]);
  const [tab, setTab]           = useState('facturi');
  const [loading, setLoading]   = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId]     = useState(null);
  const [form, setForm]         = useState(emptyFactura());

  const [showProdForm, setShowProdForm] = useState(false);
  const [editProdId, setEditProdId]     = useState(null);
  const [prodForm, setProdForm]         = useState(emptyProdus());

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [fData, pData, uData] = await Promise.all([
        universalDataService.loadFacturi(),
        universalDataService.loadProduse(),
        universalDataService.loadUtilaje(),
      ]);
      setFacturi(Array.isArray(fData) ? fData : []);
      setProduse(Array.isArray(pData) ? pData : []);
      setUtilaje(Array.isArray(uData) ? uData : []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Line helpers ──────────────────────────────────────────────────────────
  const calcLinie = (l) => {
    switch (l.categorie) {
      case 'produs':    return { ...l, total: +(((parseFloat(l.qty)||0) * (parseFloat(l.unitPrice)||0)).toFixed(2)) };
      case 'motorina':  return { ...l, total: +(((parseFloat(l.liters)||0) * (parseFloat(l.pricePerLiter)||0)).toFixed(2)) };
      case 'reparatie': return { ...l, total: parseFloat(l.repairCost) || 0 };
      case 'altele':    return { ...l, total: parseFloat(l.altCost) || 0 };
      default:          return { ...l, total: 0 };
    }
  };

  const updateLinie = (idx, key, value) => {
    setForm(prev => {
      const linii = [...prev.linii];
      let l = { ...linii[idx], [key]: value };
      if (key === 'productId') {
        const prod = produse.find(p => p.id === value);
        if (prod) { l.productName = prod.name; l.unit = prod.unit || ''; }
      }
      if (key === 'utilajId') {
        const eq = utilaje.find(u => String(u.id) === String(value));
        if (eq) l.utilajName = eq.name;
      }
      linii[idx] = calcLinie(l);
      return { ...prev, linii };
    });
  };

  const addLinie = () => setForm(p => ({ ...p, linii: [...p.linii, emptyLinie()] }));
  const removeLinie = (idx) => {
    if (form.linii.length === 1) return; // keep at least 1
    setForm(p => ({ ...p, linii: p.linii.filter((_, i) => i !== idx) }));
  };

  const totalFactura = form.linii.reduce((s, l) => s + (l.total || 0), 0);

  // ── Save invoice + route side-effects ────────────────────────────────────
  const handleSaveFactura = async () => {
    if (!form.linii.length) return alert('Adăugați cel puțin o linie.');
    const record = { ...form, id: editId || Date.now(), total: +totalFactura.toFixed(2) };
    const updated = editId ? facturi.map(f => f.id === editId ? record : f) : [...facturi, record];
    await universalDataService.saveFacturi(updated);
    setFacturi(updated);

    // ── Route motorina lines → motorina tank ─────────────────────────────
    const motorLines = record.linii.filter(l => l.categorie === 'motorina' && l.liters && l.pricePerLiter);
    if (motorLines.length) {
      const mData = await universalDataService.loadMotorina();
      const existing = mData && mData.tankCapacity ? mData : { tankCapacity: 15000, portableTankCapacity: 1000, purchases: [], dispensing: [] };
      const cleanPurchases = (existing.purchases || []).filter(p => p._invoiceId !== record.id);
      const newPurchases = motorLines.map(l => ({
        id: Date.now() + Math.random(),
        _invoiceId: record.id,
        date: record.date,
        liters: parseFloat(l.liters),
        pricePerLiter: parseFloat(l.pricePerLiter),
        supplier: record.supplier,
        note: `Factură ${record.number || record.id}`,
      }));
      await universalDataService.saveMotorina({ ...existing, purchases: [...cleanPurchases, ...newPurchases] });
    }

    // ── Route reparatie lines → utilaj repairs ────────────────────────────
    const repLines = record.linii.filter(l => l.categorie === 'reparatie' && l.utilajId && l.repairCost);
    if (repLines.length) {
      const uData = await universalDataService.loadUtilaje();
      const updatedUtilaje = (Array.isArray(uData) ? uData : []).map(eq => {
        const myLines = repLines.filter(l => String(l.utilajId) === String(eq.id));
        if (!myLines.length) return eq;
        const cleanRepairs = (eq.repairs || []).filter(r => r._invoiceId !== record.id);
        const newRepairs = myLines.map(l => ({
          id: Date.now() + Math.random(),
          _invoiceId: record.id,
          date: record.date,
          description: l.repairDesc || `Factură ${record.number || record.id}`,
          cost: parseFloat(l.repairCost),
          amortizationType: l.repairAmortType || 'current',
          amortizationYears: parseInt(l.repairAmortYears) || 1,
        }));
        return { ...eq, repairs: [...cleanRepairs, ...newRepairs] };
      });
      await universalDataService.saveUtilaje(updatedUtilaje);
      setUtilaje(updatedUtilaje);
    }

    setShowForm(false);
    setEditId(null);
    // Sync to StockAdjustment (non-blocking, non-critical)
    syncToStockAdjustment(record, produse);
  };

  const openAdd  = () => { setForm(emptyFactura()); setEditId(null); setShowForm(true); };
  const openEdit = (f) => {
    setForm({ ...f, status: f.status || 'Plătită', company: f.company || '' });
    setEditId(f.id);
    setShowForm(true);
  };

  const deleteFactura = async (id) => {
    if (!window.confirm('Ștergeți această factură?')) return;
    const updated = facturi.filter(f => f.id !== id);
    await universalDataService.saveFacturi(updated);
    setFacturi(updated);
    removeFromStockAdjustment(id);
  };

  // ── Product CRUD ──────────────────────────────────────────────────────────
  const saveProdus = async () => {
    if (!prodForm.name) return alert('Introduceți denumirea produsului.');
    const rec = { ...prodForm, id: editProdId || Date.now() };
    const updated = editProdId ? produse.map(p => p.id === editProdId ? rec : p) : [...produse, rec];
    await universalDataService.saveProduse(updated);
    setProduse(updated);
    setShowProdForm(false); setEditProdId(null);
  };

  const deleteProdus = async (id) => {
    if (!window.confirm('Ștergeți acest produs?')) return;
    await universalDataService.saveProduse(produse.filter(p => p.id !== id));
    setProduse(prev => prev.filter(p => p.id !== id));
  };

  const stockItems = buildStock(produse, facturi);

  if (loading) return <div style={S.page}><div style={{ textAlign: 'center', padding: 60 }}>Se încarcă…</div></div>;

  return (
    <div style={S.page}>

      {/* ── HEADER ─────────────────────────────────────────────────────────── */}
      <div style={S.header}>
        <div>
          <h2 style={S.title}>🧾 Facturi & Inventar</h2>
          <span style={S.sub}>{facturi.length} facturi · {produse.length} produse catalogate</span>
        </div>
        {tab === 'facturi' && <button onClick={openAdd} style={S.addBtn}>+ Adaugă Factură</button>}
        {tab === 'stoc'    && <button onClick={() => { setProdForm(emptyProdus()); setEditProdId(null); setShowProdForm(true); }} style={S.addBtn}>+ Produs Nou</button>}
      </div>

      {/* ── TABS ───────────────────────────────────────────────────────────── */}
      <div style={S.tabs}>
        {[['facturi','🧾 Facturi'], ['stoc','📦 Produse & Stoc']].map(([id, lbl]) => (
          <button key={id} onClick={() => setTab(id)} style={{ ...S.tab, ...(tab === id ? S.tabActive : {}) }}>{lbl}</button>
        ))}
      </div>

      {/* ── FACTURI TABLE ──────────────────────────────────────────────────── */}
      {tab === 'facturi' && (
        facturi.length === 0
          ? <div style={S.empty}><div style={{ fontSize: 44, marginBottom: 10 }}>🧾</div><div>Nicio factură. Apăsați <strong>+ Adaugă Factură</strong>.</div></div>
          : <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr style={S.thead}>
                    <th style={S.th}>Document</th>
                    <th style={S.th}>Data</th>
                    <th style={S.th}>Furnizor</th>
                    <th style={S.th}>Firmă</th>
                    <th style={S.th}>Status</th>
                    <th style={S.th}>Articole</th>
                    <th style={S.th}>Valoare Totală</th>
                    <th style={{ ...S.th, textAlign: 'right' }}>Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {[...facturi].sort((a, b) => new Date(b.date) - new Date(a.date)).map(f => {
                    const cats = [...new Set((f.linii || []).map(l => l.categorie))];
                    const st = STATUS_STYLE[f.status] || STATUS_STYLE['Plătită'];
                    return (
                      <tr key={f.id} style={S.trow}>
                        <td style={S.td}>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>{f.tipDoc || 'Factură'}{f.number ? ` #${f.number}` : ''}</div>
                          {f.note && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{f.note}</div>}
                        </td>
                        <td style={S.td}>{new Date(f.date).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                        <td style={{ ...S.td, fontWeight: 500 }}>{f.supplier || '—'}</td>
                        <td style={{ ...S.td, fontSize: 12, color: '#6b7280' }}>{f.company || '—'}</td>
                        <td style={S.td}>
                          <span style={{ ...st, borderRadius: 12, padding: '3px 10px', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                            {f.status || 'Plătită'}
                          </span>
                        </td>
                        <td style={S.td}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {cats.map(c => {
                              const cat = CATEGORII.find(x => x.id === c);
                              return (
                                <span key={c} style={{ background: cat?.bg || '#f3f4f6', color: cat?.color || '#374151', border: `1px solid ${cat?.color || '#d1d5db'}33`, borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 600 }}>
                                  {cat?.label || c}
                                </span>
                              );
                            })}
                          </div>
                          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>{(f.linii||[]).length} {(f.linii||[]).length === 1 ? 'linie' : 'linii'}</div>
                        </td>
                        <td style={{ ...S.td, fontWeight: 800, fontSize: 16, color: '#dc2626', whiteSpace: 'nowrap' }}>
                          {(f.total||0).toLocaleString('ro-RO', { maximumFractionDigits: 0 })} lei
                        </td>
                        <td style={{ ...S.td, textAlign: 'right' }}>
                          <button onClick={() => openEdit(f)} style={S.editBtn} title="Editează">✏️ Edit</button>
                          {' '}
                          <button onClick={() => deleteFactura(f.id)} style={S.delBtn} title="Șterge">🗑️</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#fef2f2' }}>
                    <td colSpan={6} style={{ ...S.td, fontWeight: 700, textAlign: 'right', fontSize: 14 }}>TOTAL</td>
                    <td style={{ ...S.td, fontWeight: 800, fontSize: 17, color: '#dc2626' }}>
                      {facturi.reduce((s, f) => s + (f.total||0), 0).toLocaleString('ro-RO', { maximumFractionDigits: 0 })} lei
                    </td>
                    <td style={S.td}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
      )}

      {/* ── STOC ───────────────────────────────────────────────────────────── */}
      {tab === 'stoc' && (
        stockItems.length === 0
          ? <div style={S.empty}><div style={{ fontSize: 44, marginBottom: 10 }}>📦</div><div>Niciun produs în catalog. Apăsați <strong>+ Produs Nou</strong>.</div></div>
          : <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr style={S.thead}>
                    {['Produs', 'Categorie', 'Stoc curent', 'Ultimul preț', ''].map(h => <th key={h} style={S.th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {stockItems.map(p => (
                    <tr key={p.id} style={S.trow}>
                      <td style={S.td}><strong>{p.name}</strong>{p.notes ? <div style={{ fontSize: 11, color: '#9ca3af' }}>{p.notes}</div> : null}</td>
                      <td style={S.td}><span style={{ background: '#f3f4f6', borderRadius: 10, padding: '2px 8px', fontSize: 12 }}>{p.category || '—'}</span></td>
                      <td style={{ ...S.td, fontWeight: 700, color: p.stoc > 0 ? '#166534' : '#dc2626' }}>{p.stoc.toFixed(2)} {p.unit}</td>
                      <td style={S.td}>{p.lastPrice > 0 ? `${p.lastPrice.toFixed(2)} lei/${p.unit}` : '—'}</td>
                      <td style={{ ...S.td, textAlign: 'right' }}>
                        <button onClick={() => { setProdForm({ name: p.name, category: p.category||'', unit: p.unit||'kg', notes: p.notes||'' }); setEditProdId(p.id); setShowProdForm(true); }} style={S.editBtn}>✏️ Edit</button>
                        {' '}<button onClick={() => deleteProdus(p.id)} style={S.delBtn}>🗑️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
      )}

      {/* ── FACTURA FORM MODAL ─────────────────────────────────────────────── */}
      {showForm && (
        <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <div style={S.modal}>

            {/* Modal header */}
            <div style={S.mHead}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{editId ? '✏️ Editează Factură' : '➕ Adaugă Factură Nouă'}</h3>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>Completați detaliile facturii și liniile de articole</div>
              </div>
              <button onClick={() => setShowForm(false)} style={S.closeBtn}>✕</button>
            </div>

            <div style={S.mBody}>

              {/* ── Row 1: Data | Tip | Număr | Status ───────────────────── */}
              <div style={S.formGrid4}>
                <div>
                  <label style={S.lbl}>Data</label>
                  <input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} style={S.inp} />
                </div>
                <div>
                  <label style={S.lbl}>Tip document</label>
                  <select value={form.tipDoc} onChange={e => setForm(p => ({ ...p, tipDoc: e.target.value }))} style={S.inp}>
                    {TIPURI_FACTURA.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={S.lbl}>Număr</label>
                  <input type="text" value={form.number} onChange={e => setForm(p => ({ ...p, number: e.target.value }))} style={S.inp} placeholder="ex: 001234" />
                </div>
                <div>
                  <label style={S.lbl}>Status</label>
                  <select value={form.status || 'Plătită'} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} style={{ ...S.inp, fontWeight: 600, color: STATUS_STYLE[form.status]?.color || '#166534' }}>
                    {STATUSURI.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              {/* ── Row 2: Furnizor | Firmă | Note ───────────────────────── */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
                <div>
                  <label style={S.lbl}>Furnizor</label>
                  <input type="text" value={form.supplier} onChange={e => setForm(p => ({ ...p, supplier: e.target.value }))} style={S.inp} placeholder="Agro SRL, Petrom, Service Auto…" />
                </div>
                <div>
                  <label style={S.lbl}>Firma cumpărătoare</label>
                  <input type="text" value={form.company || ''} onChange={e => setForm(p => ({ ...p, company: e.target.value }))} style={S.inp} placeholder="Firma ta / societatea" />
                </div>
                <div>
                  <label style={S.lbl}>Note</label>
                  <input type="text" value={form.note} onChange={e => setForm(p => ({ ...p, note: e.target.value }))} style={S.inp} placeholder="opțional" />
                </div>
              </div>

              {/* ── Line items table ──────────────────────────────────────── */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label style={{ fontWeight: 700, fontSize: 14, color: '#374151' }}>📦 Articole factură</label>
                <button onClick={addLinie} style={S.addLineBtn}>+ Adaugă linie</button>
              </div>

              <div style={S.lineTableWrap}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                      <th style={S.lth}>Categorie</th>
                      <th style={S.lth}>Detalii articol</th>
                      <th style={{ ...S.lth, textAlign: 'right', whiteSpace: 'nowrap' }}>Total (lei)</th>
                      <th style={{ ...S.lth, width: 32 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.linii.map((l, idx) => {
                      const cat = CATEGORII.find(c => c.id === l.categorie);
                      return (
                        <tr key={l.id} style={{ borderBottom: '1px solid #e5e7eb', verticalAlign: 'top' }}>

                          {/* Category selector */}
                          <td style={{ padding: '10px 8px', minWidth: 160 }}>
                            <select
                              value={l.categorie}
                              onChange={e => updateLinie(idx, 'categorie', e.target.value)}
                              style={{ ...S.inp, fontWeight: 700, color: cat?.color, background: cat?.bg, border: `1px solid ${cat?.color}44`, fontSize: 12, padding: '6px 8px' }}
                            >
                              {CATEGORII.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                            </select>
                          </td>

                          {/* Conditional fields */}
                          <td style={{ padding: '10px 8px' }}>

                            {/* PRODUS */}
                            {l.categorie === 'produs' && (
                              <div>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 6 }}>
                                  <div style={{ flex: 2, minWidth: 130 }}>
                                    <div style={S.fieldLbl}>Din catalog (opțional)</div>
                                    <select value={l.productId} onChange={e => updateLinie(idx, 'productId', e.target.value)} style={S.inp}>
                                      <option value="">— sau scrie mai jos —</option>
                                      {produse.map(p => <option key={p.id} value={p.id}>{p.name} ({p.unit})</option>)}
                                    </select>
                                  </div>
                                  <div style={{ flex: 2, minWidth: 120 }}>
                                    <div style={S.fieldLbl}>Nume produs *</div>
                                    <input type="text" value={l.productName} onChange={e => updateLinie(idx, 'productName', e.target.value)} style={S.inp} placeholder="ex: Roundup Max, Uree 46%" />
                                  </div>
                                  <div style={{ width: 72 }}>
                                    <div style={S.fieldLbl}>Cantitate</div>
                                    <input type="number" value={l.qty} onChange={e => updateLinie(idx, 'qty', e.target.value)} style={S.inp} placeholder="0" min="0" />
                                  </div>
                                  <div style={{ width: 55 }}>
                                    <div style={S.fieldLbl}>UM</div>
                                    <input type="text" value={l.unit} onChange={e => updateLinie(idx, 'unit', e.target.value)} style={S.inp} placeholder="kg" />
                                  </div>
                                  <div style={{ width: 80 }}>
                                    <div style={S.fieldLbl}>Lei/UM</div>
                                    <input type="number" value={l.unitPrice} onChange={e => updateLinie(idx, 'unitPrice', e.target.value)} style={S.inp} placeholder="0.00" min="0" step="0.01" />
                                  </div>
                                </div>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                  <div style={{ flex: 2, minWidth: 130 }}>
                                    <div style={S.fieldLbl}>Cultură destinație (opțional)</div>
                                    <select value={l.targetCrop || ''} onChange={e => updateLinie(idx, 'targetCrop', e.target.value)} style={S.inp}>
                                      <option value="">— selectează cultura —</option>
                                      {CROP_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                  </div>
                                  <div style={{ width: 100 }}>
                                    <div style={S.fieldLbl}>Consum/ha (opțional)</div>
                                    <input type="number" value={l.consumPerHa || ''} onChange={e => updateLinie(idx, 'consumPerHa', e.target.value)} style={S.inp} placeholder="ex: 2.5" min="0" step="0.01" />
                                  </div>
                                  <div style={{ alignSelf: 'flex-end', paddingBottom: 6, fontSize: 11, color: '#166534', fontWeight: 600 }}>
                                    🌾 → Inventar stoc
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* MOTORINA */}
                            {l.categorie === 'motorina' && (
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                <div style={{ width: 100 }}>
                                  <div style={S.fieldLbl}>Litri</div>
                                  <input type="number" value={l.liters} onChange={e => updateLinie(idx, 'liters', e.target.value)} style={S.inp} placeholder="5000" min="0" />
                                </div>
                                <div style={{ width: 100 }}>
                                  <div style={S.fieldLbl}>Lei / litru</div>
                                  <input type="number" step="0.01" value={l.pricePerLiter} onChange={e => updateLinie(idx, 'pricePerLiter', e.target.value)} style={S.inp} placeholder="7.50" min="0" />
                                </div>
                                <div style={{ width: 90 }}>
                                  <div style={S.fieldLbl}>Consum/ha (opt.)</div>
                                  <input type="number" step="0.01" value={l.consumPerHa || ''} onChange={e => updateLinie(idx, 'consumPerHa', e.target.value)} style={S.inp} placeholder="ex: 30" min="0" />
                                </div>
                                <div style={{ alignSelf: 'flex-end', paddingBottom: 6, fontSize: 11, color: '#92400e', fontWeight: 600 }}>
                                  ⛽ → Rezervor motorină
                                </div>
                              </div>
                            )}

                            {/* REPARATIE */}
                            {l.categorie === 'reparatie' && (
                              <div>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 6 }}>
                                  <div style={{ flex: 1, minWidth: 130 }}>
                                    <div style={S.fieldLbl}>Utilaj *</div>
                                    <select value={l.utilajId} onChange={e => updateLinie(idx, 'utilajId', e.target.value)} style={S.inp}>
                                      <option value="">Selectează utilajul…</option>
                                      {utilaje.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                                    </select>
                                  </div>
                                  <div style={{ flex: 2, minWidth: 130 }}>
                                    <div style={S.fieldLbl}>Descriere piesă / lucrare</div>
                                    <input type="text" value={l.repairDesc} onChange={e => updateLinie(idx, 'repairDesc', e.target.value)} style={S.inp} placeholder="Filtru ulei, curea…" />
                                  </div>
                                  <div style={{ width: 90 }}>
                                    <div style={S.fieldLbl}>Cost (lei)</div>
                                    <input type="number" value={l.repairCost} onChange={e => updateLinie(idx, 'repairCost', e.target.value)} style={S.inp} placeholder="0" min="0" />
                                  </div>
                                </div>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                                  <div style={{ width: 160 }}>
                                    <div style={S.fieldLbl}>Amortizare</div>
                                    <select value={l.repairAmortType} onChange={e => updateLinie(idx, 'repairAmortType', e.target.value)} style={S.inp}>
                                      <option value="current">Anul curent (100%)</option>
                                      <option value="multi">Mai mulți ani</option>
                                    </select>
                                  </div>
                                  {l.repairAmortType === 'multi' && (
                                    <div style={{ width: 70 }}>
                                      <div style={S.fieldLbl}>Nr. ani</div>
                                      <input type="number" min="2" max="20" value={l.repairAmortYears} onChange={e => updateLinie(idx, 'repairAmortYears', e.target.value)} style={S.inp} />
                                    </div>
                                  )}
                                  <div style={{ fontSize: 11, color: '#4c1d95', fontWeight: 600 }}>🔧 → Reparații {l.utilajName || 'utilaj'}</div>
                                </div>
                              </div>
                            )}

                            {/* ALTELE */}
                            {l.categorie === 'altele' && (
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                <div style={{ flex: 2, minWidth: 160 }}>
                                  <div style={S.fieldLbl}>Descriere</div>
                                  <input type="text" value={l.altDesc} onChange={e => updateLinie(idx, 'altDesc', e.target.value)} style={S.inp} placeholder="Electricitate, servicii, alte…" />
                                </div>
                                <div style={{ width: 100 }}>
                                  <div style={S.fieldLbl}>Cost (lei)</div>
                                  <input type="number" value={l.altCost} onChange={e => updateLinie(idx, 'altCost', e.target.value)} style={S.inp} placeholder="0" min="0" />
                                </div>
                              </div>
                            )}

                          </td>

                          {/* Total */}
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 800, fontSize: 15, color: '#1f2937', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                            {l.total.toLocaleString('ro-RO', { maximumFractionDigits: 0 })} lei
                          </td>

                          {/* Delete */}
                          <td style={{ padding: '10px 4px', verticalAlign: 'middle' }}>
                            <button onClick={() => removeLinie(idx)} style={S.delLineBtn} title="Șterge linia" disabled={form.linii.length === 1}>✕</button>
                          </td>

                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Total */}
              <div style={S.totalBar}>
                <span style={{ color: '#374151', fontSize: 14 }}>Total factură</span>
                <span style={{ fontWeight: 800, fontSize: 22, color: '#dc2626' }}>
                  {totalFactura.toLocaleString('ro-RO', { maximumFractionDigits: 0 })} lei
                </span>
              </div>

            </div>

            {/* Footer */}
            <div style={S.mFoot}>
              <button onClick={() => { setShowForm(false); setEditId(null); }} style={S.cancelBtn}>Anulează</button>
              <button onClick={handleSaveFactura} style={S.saveBtn}>💾 Salvează Factura</button>
            </div>

          </div>
        </div>
      )}

      {/* ── PRODUS FORM MODAL ─────────────────────────────────────────────── */}
      {showProdForm && (
        <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) setShowProdForm(false); }}>
          <div style={{ ...S.modal, maxWidth: 420 }}>
            <div style={S.mHead}>
              <h3 style={{ margin: 0, fontSize: 17 }}>{editProdId ? 'Editează Produs' : 'Produs Nou'}</h3>
              <button onClick={() => setShowProdForm(false)} style={S.closeBtn}>✕</button>
            </div>
            <div style={S.mBody}>
              <div style={{ marginBottom: 12 }}>
                <label style={S.lbl}>Denumire *</label>
                <input value={prodForm.name} onChange={e => setProdForm(p => ({ ...p, name: e.target.value }))} style={S.inp} placeholder="Roundup, Uree 46%, Sămânță porumb…" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                <div>
                  <label style={S.lbl}>Categorie</label>
                  <select value={prodForm.category} onChange={e => setProdForm(p => ({ ...p, category: e.target.value }))} style={S.inp}>
                    <option value="">—</option>
                    {CATEGORII_PRODUS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={S.lbl}>Unitate de măsură</label>
                  <select value={prodForm.unit} onChange={e => setProdForm(p => ({ ...p, unit: e.target.value }))} style={S.inp}>
                    {['kg', 'L', 'buc', 't', 'sac'].map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={S.lbl}>Note</label>
                <input value={prodForm.notes} onChange={e => setProdForm(p => ({ ...p, notes: e.target.value }))} style={S.inp} />
              </div>
            </div>
            <div style={S.mFoot}>
              <button onClick={() => setShowProdForm(false)} style={S.cancelBtn}>Anulează</button>
              <button onClick={saveProdus} style={S.saveBtn} disabled={!prodForm.name}>💾 Salvează</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  page:       { padding: 24, maxWidth: 1100, margin: '0 auto' },
  header:     { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  title:      { margin: 0, fontSize: 24, fontWeight: 700, color: '#1f2937' },
  sub:        { color: '#6b7280', fontSize: 13 },
  addBtn:     { padding: '10px 20px', background: '#1e40af', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14 },
  tabs:       { display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #e5e7eb' },
  tab:        { padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, color: '#6b7280', borderBottom: '2px solid transparent', marginBottom: -2 },
  tabActive:  { color: '#1e40af', borderBottomColor: '#1e40af', fontWeight: 700, background: '#eff6ff', borderRadius: '4px 4px 0 0' },
  // Invoice table
  tableWrap:  { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' },
  table:      { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  thead:      { background: '#1e293b' },
  th:         { padding: '11px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#fff', letterSpacing: '0.04em', textTransform: 'uppercase' },
  trow:       { borderBottom: '1px solid #f1f5f9', background: '#fff' },
  td:         { padding: '12px 14px', color: '#374151', verticalAlign: 'middle' },
  // Modal
  overlay:    { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px 16px', overflowY: 'auto' },
  modal:      { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 900, marginTop: 10, boxShadow: '0 24px 60px rgba(0,0,0,0.28)' },
  mHead:      { padding: '18px 22px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', background: '#f8fafc', borderRadius: '14px 14px 0 0' },
  closeBtn:   { background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af', padding: 4 },
  mBody:      { padding: '20px 22px', maxHeight: '74vh', overflowY: 'auto' },
  mFoot:      { padding: '14px 22px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10, background: '#f8fafc', borderRadius: '0 0 14px 14px' },
  // Form fields
  formGrid4:  { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 12 },
  lbl:        { display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' },
  inp:        { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, width: '100%', boxSizing: 'border-box', outline: 'none' },
  // Line items table
  lineTableWrap: { border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', marginBottom: 0 },
  lth:        { padding: '9px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: '0.04em', textTransform: 'uppercase' },
  fieldLbl:   { fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 3, letterSpacing: '0.03em' },
  addLineBtn: { padding: '6px 14px', background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  delLineBtn: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', padding: '4px 8px', color: '#dc2626', fontSize: 13 },
  totalBar:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fef2f2', border: '1px solid #fecaca', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '12px 16px' },
  // Bottom buttons
  cancelBtn:  { padding: '10px 20px', background: '#f1f5f9', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#374151' },
  saveBtn:    { padding: '10px 28px', background: '#1e40af', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14 },
  // Misc
  empty:      { textAlign: 'center', padding: '70px 20px', color: '#6b7280' },
  editBtn:    { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, cursor: 'pointer', padding: '5px 10px', fontSize: 13, fontWeight: 600, color: '#1e40af' },
  delBtn:     { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', padding: '5px 8px', fontSize: 13, color: '#dc2626' },
};

export default Inventory;
